import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { qpxToast } from '@quipex/core/utils';
import {
    hasSentMessage,
    injectQuippySession,
    sameQuippyScope,
    type QuippyHostActions,
    type QuippyChatRef,
    type QuippyScope,
    type QuippyScopeSelection,
} from '@quipex/feature/quippy-conversation';
import type { AppQuippyDialogLauncher } from './dialog/launcher';

@Injectable()
export class AppQuippyHostActions implements QuippyHostActions {
    private readonly router = inject(Router);
    private readonly session = injectQuippySession();
    private dialogChatRef: QuippyChatRef | null = null;
    private dialog: AppQuippyDialogLauncher | undefined;
    private readonly pendingNew = signal<
        { readonly scope: QuippyScope; readonly ref: QuippyChatRef } | undefined
    >(undefined);

    public restoreDialogChat(): QuippyChatRef | null {
        const ref = this.dialogChatRef;
        const conversation = ref ? this.session.read(ref) : null;
        if (!ref || !conversation) {
            this.dialogChatRef = null;
            return null;
        }
        const pageRoute = this.router.createUrlTree([
            ...chatRoute(conversation.scope, ref.chatId),
        ]);
        if (
            this.router.isActive(pageRoute, {
                paths: 'exact',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored',
            })
        )
            return null;
        return ref;
    }

    public rememberDialogChat(ref: QuippyChatRef): void {
        this.dialogChatRef = ref;
    }

    public registerDialog(dialog: AppQuippyDialogLauncher): void {
        this.dialog = dialog;
    }

    public async openPack(
        buildingId: string,
        packId?: string
    ): Promise<boolean> {
        const navigated = await this.navigate([
            '/buildings',
            buildingId,
            'packs',
            ...(packId ? [packId] : []),
        ]);
        if (navigated) this.dialog?.close();
        return navigated;
    }

    public async navigateToChat(ref: QuippyChatRef): Promise<boolean> {
        const conversation = this.session.read(ref);
        if (!conversation) return false;
        return this.navigate(chatRoute(conversation.scope, ref.chatId));
    }

    public async openDialog(ref: QuippyChatRef): Promise<void> {
        const conversation = this.session.read(ref);
        if (!conversation) return;
        await this.dialog?.open(this.selection(conversation.scope), ref);
    }

    public async newPageChat(
        scope: QuippyScope,
        ref = this.session.create(scope)
    ): Promise<boolean> {
        const pending = { scope, ref };
        this.pendingNew.set(pending);
        const navigated = await this.navigate(chatRoute(scope, 'new'));
        if (!navigated && this.pendingNew() === pending)
            this.pendingNew.set(undefined);
        return navigated;
    }

    public takeNewPageRef(scope: QuippyScope): QuippyChatRef | null {
        const pending = this.pendingNew();
        if (!pending || !sameQuippyScope(pending.scope, scope)) return null;
        this.pendingNew.set(undefined);
        return pending.ref;
    }

    public async replaceNewRoute(ref: QuippyChatRef): Promise<boolean> {
        const conversation = this.session.read(ref);
        if (!conversation) return false;
        return this.navigate(chatRoute(conversation.scope, ref.chatId), true);
    }

    public async openChatInPageAndCloseDialog(
        ref: QuippyChatRef
    ): Promise<boolean> {
        const conversation = this.session.read(ref);
        if (!conversation) return false;
        const navigated = hasSentMessage(conversation.thread)
            ? await this.navigateToChat(ref)
            : await this.newPageChat(conversation.scope, ref);
        if (navigated) {
            this.dialogChatRef = null;
            this.dialog?.close();
        }
        return navigated;
    }

    private async navigate(
        commands: readonly string[],
        replaceUrl = false
    ): Promise<boolean> {
        try {
            const destination = this.router.createUrlTree([...commands]);
            const activeMatch = {
                paths: 'exact',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored',
            } as const;
            if (this.router.isActive(destination, activeMatch)) return true;
            const navigated = await this.router.navigate([...commands], {
                replaceUrl,
            });
            if (!navigated || !this.router.isActive(destination, activeMatch)) {
                reportNavigationFailure();
                return false;
            }
            return true;
        } catch {
            reportNavigationFailure();
            return false;
        }
    }

    private selection(scope: QuippyScope): QuippyScopeSelection {
        if (scope.kind === 'portfolio') return scope;
        const details = this.session.buildingDetails(scope);
        return {
            kind: 'building',
            building: {
                id: scope.buildingId,
                name: details?.name ?? 'Selected building',
                address: details?.address ?? '',
            },
        };
    }
}

export function chatRoute(
    scope: QuippyScope,
    chatId: string
): readonly string[] {
    return scope.kind === 'portfolio'
        ? ['/chat', chatId]
        : ['/buildings', scope.buildingId, 'chat', chatId];
}

function reportNavigationFailure(): void {
    // Router failures do not pass through HTTP auto-toast.
    // eslint-disable-next-line agentic/prefer-auto-error-toast
    qpxToast.error('Unable to open Ask Quippy. Try again.');
}
